/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text, TextInput } from '@/shared/ui';
import { DraftRecoveryNotice, formatDraftTime } from '@/shared/ui/components/form';
import { saveErrorKind } from '@/shared/utils/save-error-kind';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import backendClientsCrud from '@services/housien/clients';
import { maskPhone } from '../utils/masks';
import { useQuickClientDraft, type QuickClientDraftValues } from './useQuickClientDraft';
import type { Client } from '@services/requests/housien-models/Client';

/**
 * Extracts a human-readable error detail from an unknown thrown value.
 *
 * @param err - The caught error, of unknown shape.
 * @param fallbackMsg - The message to use when no detail is present.
 * @returns The resolved error detail string.
 */
function extractErrorDetail(err: unknown, fallbackMsg: string): string {
  const detail =
    (err as { body?: { detail?: string }; message?: string })?.body?.detail ||
    (err as { message?: string })?.message ||
    fallbackMsg;
  return typeof detail === 'string' ? detail : fallbackMsg;
}

/**
 * Creates an individual client via the backend clients CRUD.
 *
 * @param agencyId - The owning agency identifier.
 * @param fullName - The client's full name.
 * @param phone - The client's phone number.
 * @param email - The client's email address.
 * @returns The created client payload including its identifier.
 */
async function createClient(agencyId: string, fullName: string, phone: string, email: string): Promise<Client> {
  return backendClientsCrud(agencyId).insert({
    body: {
      identity: { person_type: 'individual' },
      personal_info: { full_name: fullName },
      assignment: {
        contacts: [
          ...(email ? [{ type: 'email', value: email }] : []),
          ...(phone ? [{ type: 'phone', value: phone }] : []),
        ],
      },
    },
  });
}

/**
 * The message for a failed create: an anti-bot rejection gets its own text
 * (and only claims the data was kept when it really was).
 *
 * @param err - The thrown value.
 * @param t - The clients translator.
 * @param draftKept - Whether the typed data was stored locally.
 * @returns The user-facing message.
 */
function failureMessage(err: unknown, t: (key: string) => string, draftKept: boolean): string {
  if (saveErrorKind(err) === 'antibot') {
    return t(draftKept ? 'quickClient.errorAntiBotKept' : 'quickClient.errorAntiBot');
  }
  return extractErrorDetail(err, t('quickClient.errorCreating'));
}

export interface QuickClientFormProps {
  agencyId: string;
  onCreated: (clientId: string, clientName: string) => void;
  onCancel: () => void;
}

/**
 * Inline quick-create form for an individual client (name required).
 *
 * @param props - Agency id and create/cancel handlers.
 * @returns Quick client form panel.
 */
export function QuickClientForm({ agencyId, onCreated, onCancel }: QuickClientFormProps) {
  const { t, i18n } = useTranslation('features/clients');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const canSave = fullName.trim().length >= 2;
  const applyDraft = useCallback((d: QuickClientDraftValues) => {
    setFullName(d.fullName);
    setPhone(d.phone);
    setEmail(d.email);
  }, []);
  const draft = useQuickClientDraft(agencyId, { fullName, phone, email }, applyDraft);

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError('');
    try {
      const result = await createClient(agencyId, fullName.trim(), phone.trim(), email.trim());
      draft.clear();
      const clientId = result?.metadata?.client_id || '';
      onCreated(clientId, fullName.trim());
    } catch (err: unknown) {
      setError(failureMessage(err, t, draft.keep()));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      data-testid="quick-client-form"
      data-layout="comfortable"
      className="w-full"
    >
      <Text className="mb-(--space-4) text-[length:var(--text-app-heading)] font-semibold text-(--text-primary)">
        {t('quickClient.title')}
      </Text>
      {draft.offer ? (
        <DraftRecoveryNotice
          message={t('quickClient.draftRecovered', { when: formatDraftTime(draft.offer.savedAt, i18n.language) })}
          restoreLabel={t('quickClient.draftRestore')}
          discardLabel={t('quickClient.draftDiscard')}
          onRestore={draft.restore}
          onDiscard={draft.discard}
        />
      ) : null}
      <div className="flex w-full flex-col gap-(--space-4)">
        <TextInput
          label={t('quickClient.fullName')}
          required
          value={fullName}
          onChange={e => setFullName(e.target.value)}
          placeholder={t('quickClient.fullNamePlaceholder')}
          maxLength={FORM_FIELD_LIMITS.shortName}
          autoFocus
        />
        <TextInput
          label={t('quickClient.phone')}
          value={phone}
          inputMode="tel"
          onChange={e => setPhone(maskPhone(e.target.value))}
          placeholder={t('quickClient.phonePlaceholder')}
          maxLength={FORM_FIELD_LIMITS.phoneDigits + 1}
        />
        <TextInput
          label={t('quickClient.email')}
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t('quickClient.emailPlaceholder')}
          maxLength={FORM_FIELD_LIMITS.email}
        />
        {error ? (
          <Text className="text-[length:var(--text-body-sm)] text-(--status-error)">{error}</Text>
        ) : null}
        <div className="mt-(--space-1) flex flex-wrap justify-end gap-(--space-3)">
          <Button variant="outline" onClick={onCancel}>
            {t('quickClient.cancel')}
          </Button>
          <Button
            variant="solid"
            onClick={() => void handleSave()}
            disabled={!canSave || isSaving}
          >
            {isSaving ? t('quickClient.saving') : t('quickClient.createAndSelect')}
          </Button>
        </div>
      </div>
    </div>
  );
}
